'use client';

import { type components } from '~/fecom-api/schema';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Heading } from '../common/heading';
import { AddToBasketButton } from './add-to-basket-button';
import { QuantityInput } from '../basket/quantity-input';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import StockStatus from './stock-status';
import { type ProductDetailsRouteResponse } from '~/app/api/product/[itemCode]/route';
import { Skeleton } from '../ui/skeleton';
import { cn } from '~/lib/utils';
import { ChevronDown, ChevronUp, EyeIcon } from 'lucide-react';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useQuantity } from '~/hooks/product/use-quantity';
import { ProductTags } from './product-tags';
import { ProductDetailsPrice } from './product-details-price';
import { DangerousHtml } from '../common/dangerous-html';
import { ProductImageCarousel } from './product-image-carousel';
import { ProductsILoveButton } from './products-i-love-button';
import { GtmEventComponent } from '../head/gtm-event-component';
import {
  gtmAddProductsToCart,
  mapProductToGtmItem,
} from '~/utils/gtm/gtm-helpers';
import { InlineLink } from '../common/inline-link';

export type ProductListQuickViewProps = {
  title: string;
  initalProduct?: components['schemas']['Product'];
  fecomMediaBaseUrl: string;
  triggerClassName?: string;
};

export function ProductListQuickView(props: ProductListQuickViewProps) {
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const product = props.initalProduct;

  const [productDetails, setProduct] = React.useState<
    components['schemas']['ProductDetails'] | null
  >(null);

  const productUrl = product?.urlPath;

  useEffect(() => {
    const getData = async () => {
      if (open === false) return;
      try {
        const res = await fetch(
          '/api/product/' + props.initalProduct?.itemCode,
        );
        const data = (await res.json()) as ProductDetailsRouteResponse;

        if ('success' in data) {
          setProduct(data.success);
        } else {
          console.error('Error fetching product data:', data.error);
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
      setIsLoading(false);
    };

    void getData();
  }, [props.initalProduct?.itemCode, open]);

  const {
    onQuantityChange,
    minQuantity,
    quantity,
    isLoading: isBasketLoading,
    resetQuantity,
  } = useQuantity({
    catalogueRef: props.initalProduct?.catalogueRef,
    minimumOrderQuantity: props.initalProduct?.minOrderQuantity,
  });

  const [showMoreInfo, setShowMoreInfo] = useState(false);

  if (!product) {
    return null;
  }

  const description = productDetails?.description; //.replace(/(<([^>]+)>)/gi, '');

  const intitalProductImages: components['schemas']['ProductImage'][] =
    product.imageId ? [{ index: 9999, name: product.imageId }] : [];

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        resetQuantity?.();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="info-outline"
          size="sm"
          className={cn(
            'group ml-auto h-8 min-w-6 rounded-pill border-0 bg-white/50 px-1.5 hover:bg-white hover:shadow-md focus-visible:bg-white',
            props.triggerClassName,
          )}
        >
          <span className="w-0 overflow-hidden text-base font-semibold leading-none opacity-0 transition-all group-hover:w-24 group-hover:opacity-100 group-focus-visible:w-24 group-focus-visible:opacity-100">
            Quick View{' '}
          </span>
          <EyeIcon className="size-5 fill-white" />
          <span className="sr-only">{props.title}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-svh flex-col sm:max-w-2xl">
        {props.initalProduct && (
          <GtmEventComponent
            event={{
              event: 'view_item',
              ecommerce: {
                currency: 'GBP',
                value: props.initalProduct.priceIncVat,
                valueExcVat: props.initalProduct.priceExcVat,
                items: [
                  mapProductToGtmItem({
                    product: props.initalProduct,
                    quantity: props.initalProduct.minOrderQuantity,
                    overrides: {
                      item_list_id: 'quick-view',
                      item_list_name: 'Quick View',
                    },
                  }),
                ],
              },
            }}
          />
        )}
        <DialogHeader>
          <DialogTitle>Quick View</DialogTitle>
        </DialogHeader>
        <div className="-mx-2 overflow-auto px-2">
          <div className="grid items-start gap-6 py-4 sm:grid-cols-2 sm:grid-rows-[auto,_auto] md:gap-x-6 md:gap-y-4">
            <div className="relative">
              <ProductImageCarousel
                images={productDetails?.imagesList ?? intitalProductImages}
                fecomMediaBaseUrl={props.fecomMediaBaseUrl}
                variant="quick-view"
              />
              <ProductsILoveButton
                catRef={product.catalogueRef}
                className="absolute right-2 top-2 h-6 w-6"
                variant="main"
                isInitialFavourite={product.isFavourite}
              />
            </div>

            <div className="row-span-2">
              <DialogDescription asChild>
                <h2 className="text-lg font-semibold leading-6 lg:text-xl">
                  {product?.productName}
                </h2>
              </DialogDescription>
              {product.brandName && (
                <p className="mt-2 text-sm">{product.brandName}</p>
              )}
              <p
                className="mt-4 text-sm leading-7 sm:text-base"
                data-testid="product-code"
              >
                Product code: <b>{product.catalogueRef}</b>
              </p>
              {product.stockInfo && (
                <StockStatus
                  stockInfo={product.stockInfo}
                  isDirectDelivery={productDetails?.isDirectDelivery}
                  directDeliveryText={product.directDeliveryText}
                  classname="mt-4 text-black"
                />
              )}
              <div className="mt-4">
                <ProductTags
                  roundels={props.initalProduct?.roundels}
                  variant="pdp"
                  limit={2}
                  isContractPrice={props.initalProduct?.isContractPrice}
                  isRestricted={props.initalProduct?.isRestricted}
                  minOrderQuantity={props.initalProduct?.minOrderQuantity}
                  isDiscountPrice={props.initalProduct?.isDiscountPrice}
                  isDiscountExcluded={props.initalProduct?.isDiscountExcluded}
                />
              </div>
              <ProductDetailsPrice
                className="mt-6"
                priceIncVat={product.priceIncVat}
                priceExcVat={product.priceExcVat}
                wasPriceIncVat={product.wasPriceIncVat}
                wasPriceExcVat={product.wasPriceExcVat}
                isPriceBreakPrice={product.isPriceBreakPrice}
              />

              {product.isVariant ? (
                <p className="mt-4 font-normal leading-normal text-muted-foreground">
                  <InlineLink
                    href={product.urlPath}
                    variant="inherit"
                    className="no-underline hover:underline"
                    tabIndex={-1}
                  >
                    + More Options
                  </InlineLink>
                </p>
              ) : null}
              <div className="mt-6 flex gap-6">
                <QuantityInput
                  key={open ? 'open' : 'closed'}
                  quantity={quantity}
                  isLoading={isBasketLoading}
                  disabled={
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    props.initalProduct?.isRestricted ||
                    props.initalProduct?.isTemporarilyUnavailable
                  }
                  onChange={onQuantityChange}
                  size="quickview"
                  minQuantity={minQuantity}
                />
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {props.initalProduct?.isRestricted ||
                props.initalProduct?.isTemporarilyUnavailable ? (
                  <Button className="grow" disabled>
                    {product.isRestricted ? 'Restricted Product' : 'Back Soon'}
                  </Button>
                ) : (
                  <AddToBasketButton
                    className="grow"
                    itemCode={product.catalogueRef}
                    productName={product.productName}
                    quantity={quantity}
                    onSuccess={() => {
                      gtmAddProductsToCart({
                        products: [[product, minQuantity]],
                        currency: 'GBP',
                        itemListId: 'quick-view',
                        itemListName: 'Quick View',
                      });
                    }}
                  />
                )}
              </div>
              <Button variant="outline" className="mt-5 w-full" asChild>
                <Link href={productUrl ?? '#'}>See More Details</Link>
              </Button>
            </div>

            <div className="">
              <Heading tag="h3" size="custom" className="text-base leading-6">
                Product Details
              </Heading>
              {isLoading ? (
                <Skeleton className="h-24" />
              ) : (
                <div className="mt-4 flex flex-col gap-2">
                  <DangerousHtml
                    className={cn(
                      'prose prose-base leading-normal text-current marker:text-current',
                      {
                        'line-clamp-2': !showMoreInfo,
                      },
                    )}
                    html={description ?? 'No description'}
                  />

                  <Button
                    variant="link"
                    size="custom"
                    className="ml-auto text-info"
                    onClick={() => setShowMoreInfo(!showMoreInfo)}
                  >
                    {!showMoreInfo ? (
                      <>
                        Read more <ChevronDown />
                      </>
                    ) : (
                      <>
                        Show Less
                        <ChevronUp />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
